import React from "react";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/** Label + control + hint/error wrapper. Children render the control; `essential` shows the AI-minimum badge. */
export const Field = ({ id, label, hint, error, essential = false, optional = false, className = "", children }) => {
  const { t } = useTranslation();
  return (
    <div className={cn("space-y-1.5", className)}>
      {label && (
        <div className="flex items-center gap-2">
          <Label htmlFor={id} className="text-sm font-medium">{label}</Label>
          {essential && <Badge variant="outline" className="border-sky-200 bg-sky-50 px-1.5 py-0 text-[10px] font-medium uppercase tracking-wide text-sky-900">{t("brandBrain.essential")}</Badge>}
          {optional && <span className="text-xs text-muted-foreground">({t("common.optional")})</span>}
        </div>
      )}
      {children}
      {error ? (
        <p className="text-xs text-destructive" role="alert" data-testid={`${id}-error`}>{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
};

export const TextField = ({ id, label, hint, error, essential, optional, value, onChange, type = "text", placeholder, testId, className, ...rest }) => (
  <Field id={id} label={label} hint={hint} error={error} essential={essential} optional={optional} className={className}>
    <Input id={id} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId || id} aria-invalid={!!error} className={cn(error && "border-destructive focus-visible:ring-destructive")} {...rest} />
  </Field>
);

export const TextareaField = ({ id, label, hint, error, essential, optional, value, onChange, placeholder, rows = 4, testId, className, ...rest }) => (
  <Field id={id} label={label} hint={hint} error={error} essential={essential} optional={optional} className={className}>
    <Textarea id={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows} data-testid={testId || id} aria-invalid={!!error} className={cn("resize-y", error && "border-destructive focus-visible:ring-destructive")} {...rest} />
  </Field>
);

/**
 * Local text buffer for list fields (comma- or line-separated).
 *
 * The parent receives a normalised array (trimmed items, empties removed) on every change, but the
 * text the user is typing is kept verbatim: trailing separators, spaces, accents, dead-key
 * composition and newlines are never rewritten under the cursor. The buffer is only re-synchronised
 * when the value changes *externally* (load from server, reset, row removal), i.e. when the incoming
 * array differs from the last one this field emitted.
 */
const useListBuffer = (value, join, parse, onChange) => {
  const [text, setText] = React.useState(() => join(value || []));
  const emitted = React.useRef(null);
  React.useEffect(() => {
    const incoming = JSON.stringify(value || []);
    const last = emitted.current === null ? null : JSON.stringify(emitted.current);
    const ownEcho = last !== null && (incoming === last || incoming.toLowerCase() === last.toLowerCase());
    if (!ownEcho) {
      const joined = join(value || []);
      if (joined !== text) setText(joined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const handleChange = (raw) => {
    setText(raw);
    const parsed = parse(raw);
    emitted.current = parsed;
    onChange(parsed);
  };
  return [text, handleChange];
};

const joinLines = (arr) => arr.join("\n");
const parseLines = (raw) => raw.split("\n").map((s) => s.trim()).filter(Boolean);
const joinCsv = (arr) => arr.join(", ");
const parseCsv = (raw) => raw.split(",").map((s) => s.trim()).filter(Boolean);

/** Array of strings edited as one item per line. */
export const LinesField = ({ id, label, hint, error, essential, optional, value, onChange, placeholder, rows = 4, testId }) => {
  const { t } = useTranslation();
  const [text, handleChange] = useListBuffer(value, joinLines, parseLines, onChange);
  return (
    <Field id={id} label={label} hint={hint || t("common.perLine")} error={error} essential={essential} optional={optional}>
      <Textarea
        id={id}
        value={text}
        rows={rows}
        placeholder={placeholder}
        data-testid={testId || id}
        className="resize-y"
        onChange={(e) => handleChange(e.target.value)}
      />
    </Field>
  );
};

/** Bare comma-separated input (no label) — used inside repeaters. */
export const CsvInput = ({ id, value, onChange, placeholder, testId, className }) => {
  const [text, handleChange] = useListBuffer(value, joinCsv, parseCsv, onChange);
  return <Input id={id} value={text} placeholder={placeholder} data-testid={testId || id} className={className} onChange={(e) => handleChange(e.target.value)} />;
};

/** Array of strings edited as a comma separated list. */
export const CsvField = ({ id, label, hint, error, essential, optional, value, onChange, placeholder, testId }) => {
  const { t } = useTranslation();
  return (
    <Field id={id} label={label} hint={hint || t("common.commaSeparated")} error={error} essential={essential} optional={optional}>
      <CsvInput id={id} value={value} onChange={onChange} placeholder={placeholder} testId={testId || id} />
    </Field>
  );
};
